import React, { createContext, useContext, useMemo } from 'react';
import {
  View,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { AdminDataTableDensity } from '@/constants/shared/theme/types';
import { WEB_STYLES } from '@/utils/webStyles';

export type GlassDataTableDensity = 'compact' | 'comfortable';

export interface GlassDataTableProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  density?: GlassDataTableDensity;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  footerStyle?: StyleProp<ViewStyle>;
}

const DEFAULT_DENSITY_VALUES: AdminDataTableDensity = {
  cellPaddingY: 14,
  headerPaddingY: 16,
};

const GlassDataTableDensityContext = createContext<AdminDataTableDensity>(DEFAULT_DENSITY_VALUES);

/**
 * Read the resolved cell/header padding for the nearest GlassDataTable density.
 * Row primitives (DesktopDataTable rows, mobile TableRow) consume this so that
 * a `density="compact"` table tightens every row inside it.
 */
export function useGlassDataTableDensity(): AdminDataTableDensity {
  return useContext(GlassDataTableDensityContext);
}

/**
 * Glass-styled outer shell for admin data tables. Phase 3.1 wraps DesktopDataTable
 * (and the mobile DataTable) in this primitive — for now it stands alone and renders
 * any content (rows, headers, filters) inside a theme-aware glass card.
 */
export const GlassDataTable: React.FC<GlassDataTableProps> = ({
  children,
  header,
  footer,
  density = 'comfortable',
  style,
  contentStyle,
  headerStyle,
  footerStyle,
}) => {
  const styles = useStyles();
  const { components } = useThemeColors();
  const densityValues = useMemo(
    () => components.admin.dataTableDensity?.[density] ?? DEFAULT_DENSITY_VALUES,
    [components.admin.dataTableDensity, density],
  );

  return (
    <GlassDataTableDensityContext.Provider value={densityValues}>
      <View style={[styles.container, style]}>
        {header ? (
          <View
            style={[
              styles.header,
              { paddingVertical: densityValues.headerPaddingY },
              headerStyle,
            ]}
          >
            {header}
          </View>
        ) : null}
        <View style={[styles.content, contentStyle]}>{children}</View>
        {footer ? <View style={[styles.footer, footerStyle]}>{footer}</View> : null}
      </View>
    </GlassDataTableDensityContext.Provider>
  );
};

GlassDataTable.displayName = 'GlassDataTable';

/**
 * Helper for callers rendering rows: returns a background style for zebra striping
 * using admin.dataTable.row / rowAlt tokens.
 */
export function useGlassDataTableRowStyle(rowIndex: number): StyleProp<ViewStyle> {
  const styles = useStyles();
  return rowIndex % 2 === 0 ? styles.row : styles.rowAlt;
}

const useStyles = () =>
  useThemedStyles(({ components }) =>
    StyleSheet.create({
      container: {
        backgroundColor: components.admin.dataTable.bg,
        borderWidth: 1,
        borderColor: components.admin.dataTable.border,
        borderRadius: 16,
        overflow: 'hidden',
        ...(WEB_STYLES.backdropBlur(16) as object),
      },
      header: {
        backgroundColor: components.admin.dataTable.headerBg,
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
      },
      content: {
        backgroundColor: 'transparent',
      },
      footer: {
        borderTopWidth: 1,
        borderTopColor: components.admin.dataTable.border,
      },
      row: {
        backgroundColor: components.admin.dataTable.row,
      },
      rowAlt: {
        backgroundColor: components.admin.dataTable.rowAlt,
      },
    }),
  );
